// Image caching service with tiered storage strategy
// Handles remote image caching with IndexedDB for persistent images and Cache API for disposable images

import { imageRepository } from '../db/repositories.js';
import { estimateSize, formatBytes } from '../cache/cache-utils.js';
import { OfflineConfig, isPersistentImageType } from '../config/offline-config.js';

class ImageCacheService {
  constructor() {
    this.repository = imageRepository;
    this.memoryCache = new Map();
    this.maxMemoryCacheSize = OfflineConfig.storage.maxMemoryCacheSize;
    this.maxStorageSize = OfflineConfig.storage.maxImageStorageBytes;
    this.pendingFetches = new Map(); // Prevent duplicate fetches
    this.cacheApiCacheName = 'gym-disposable-images';
  }

  /**
   * Determine if image should use persistent storage (IndexedDB) or disposable (Cache API)
   */
  getStorageTier(imageType) {
    if (isPersistentImageType(imageType)) {
      return 'persistent'; // IndexedDB
    }
    return 'disposable'; // Cache API
  }

  /**
   * Get image from cache or fetch and cache it
   */
  async getImage(url, options = {}) {
    const { forceRefresh = false, skipCache = false, imageType = 'default' } = options;
    const storageTier = this.getStorageTier(imageType);

    // Check memory cache first
    if (!forceRefresh && !skipCache) {
      const memCached = this.memoryCache.get(url);
      if (memCached) {
        return memCached;
      }
    }

    // Check appropriate cache based on tier
    if (!forceRefresh && !skipCache) {
      if (storageTier === 'persistent') {
        const cached = await this.repository.getImage(url);
        if (cached && cached.blob) {
          this.memoryCache.set(url, cached);
          this.cleanupMemoryCache();
          return cached;
        }
      } else {
        // Check Cache API for disposable images
        const cached = await this.getFromCacheAPI(url);
        if (cached) {
          const imageData = { url, blob: cached, source: 'cache-api' };
          this.memoryCache.set(url, imageData);
          this.cleanupMemoryCache();
          return imageData;
        }
      }
    }

    // Fetch from network
    if (this.pendingFetches.has(url)) {
      return this.pendingFetches.get(url);
    }

    const fetchPromise = this.fetchAndCacheImage(url, { ...options, storageTier });
    this.pendingFetches.set(url, fetchPromise);

    try {
      const result = await fetchPromise;
      return result;
    } finally {
      this.pendingFetches.delete(url);
    }
  }

  /**
   * Get image from Cache API
   */
  async getFromCacheAPI(url) {
    try {
      const cache = await caches.open(this.cacheApiCacheName);
      const response = await cache.match(url);
      
      if (response) {
        const blob = await response.blob();
        return blob;
      }
      
      return null;
    } catch (error) {
      console.error('[ImageCache] Cache API fetch failed:', error);
      return null;
    }
  }

  /**
   * Fetch image from network and cache it
   */
  async fetchAndCacheImage(url, options = {}) {
    const { storageTier = 'disposable', ttl, imageType } = options;

    try {
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      const blob = await response.blob();

      // Check blob size
      if (blob.size > OfflineConfig.images.maxBlobSize) {
        console.warn('[ImageCache] Image exceeds max size:', blob.size);
        // Could resize here, but for now just log warning
      }

      // Cache based on tier
      if (storageTier === 'persistent') {
        await this.repository.saveImage(url, blob, {
          ttl: ttl || 30 * 24 * 60 * 60 * 1000, // 30 days for persistent
          source: 'network',
          imageType,
        });
      } else {
        await this.cacheInCacheAPI(url, blob, ttl);
      }

      // Cache in memory
      const cached = { url, blob, storageTier };
      this.memoryCache.set(url, cached);
      this.cleanupMemoryCache();

      // Cleanup storage if needed
      if (storageTier === 'persistent') {
        await this.cleanupStorage();
      }

      return cached;
    } catch (error) {
      console.error('[ImageCache] Fetch failed:', error);
      
      // Return fallback if available
      if (options.fallbackUrl) {
        return this.getImage(options.fallbackUrl, options);
      }

      throw error;
    }
  }

  /**
   * Cache image in Cache API
   */
  async cacheInCacheAPI(url, blob, ttl) {
    try {
      const cache = await caches.open(this.cacheApiCacheName);
      const response = new Response(blob, {
        headers: {
          'Content-Type': blob.type,
          'Content-Length': blob.size.toString(),
          'Cache-Control': ttl ? `max-age=${Math.floor(ttl / 1000)}` : 'max-age=604800', // 7 days default
        },
      });
      await cache.put(url, response);
    } catch (error) {
      console.error('[ImageCache] Cache API store failed:', error);
    }
  }

  /**
   * Preload images for later use
   */
  async preloadImages(urls, options = {}) {
    const results = await Promise.allSettled(
      urls.map(url => this.getImage(url, { ...options, lazy: true }))
    );

    const successful = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    return { successful, failed };
  }

  /**
   * Cache image from blob (e.g., from file upload)
   */
  async cacheImage(url, blob, options = {}) {
    const { imageType = 'default' } = options;
    const storageTier = this.getStorageTier(imageType);

    if (storageTier === 'persistent') {
      await this.repository.saveImage(url, blob, {
        ttl: options.ttl || 30 * 24 * 60 * 60 * 1000,
        source: options.source || 'upload',
        imageType,
      });
    } else {
      await this.cacheInCacheAPI(url, blob, options.ttl);
    }

    // Cache in memory
    const cached = { url, blob, storageTier };
    this.memoryCache.set(url, cached);
    this.cleanupMemoryCache();

    return cached;
  }

  /**
   * Check if image is cached
   */
  async isCached(url, imageType = 'default') {
    const storageTier = this.getStorageTier(imageType);

    // Check memory
    if (this.memoryCache.has(url)) {
      return true;
    }

    // Check appropriate storage
    if (storageTier === 'persistent') {
      return await this.repository.isCached(url);
    } else {
      const cached = await this.getFromCacheAPI(url);
      return !!cached;
    }
  }

  /**
   * Invalidate cached image
   */
  async invalidate(url, imageType = 'default') {
    const storageTier = this.getStorageTier(imageType);

    // Remove from memory
    this.memoryCache.delete(url);

    // Remove from appropriate storage
    if (storageTier === 'persistent') {
      const key = this.repository.generateKey(url);
      await this.repository.hardDelete(key);
    } else {
      await this.invalidateFromCacheAPI(url);
    }

    return true;
  }

  /**
   * Invalidate from Cache API
   */
  async invalidateFromCacheAPI(url) {
    try {
      const cache = await caches.open(this.cacheApiCacheName);
      await cache.delete(url);
    } catch (error) {
      console.error('[ImageCache] Cache API invalidation failed:', error);
    }
  }

  /**
   * Invalidate multiple images
   */
  async invalidateBatch(urls, imageType = 'default') {
    await Promise.all(urls.map(url => this.invalidate(url, imageType)));
    return urls.length;
  }

  /**
   * Clear all cached images
   */
  async clearAll() {
    this.memoryCache.clear();
    await this.repository.clear();
    
    // Clear Cache API
    try {
      await caches.delete(this.cacheApiCacheName);
    } catch (error) {
      console.error('[ImageCache] Cache API clear failed:', error);
    }
    
    return true;
  }

  /**
   * Cleanup memory cache (LRU)
   */
  cleanupMemoryCache() {
    if (this.memoryCache.size <= this.maxMemoryCacheSize) {
      return;
    }

    const entries = Array.from(this.memoryCache.entries());
    const toRemove = entries.slice(0, entries.length - this.maxMemoryCacheSize);
    
    toRemove.forEach(([url, imageData]) => {
      // Revoke blob URL if exists
      if (imageData.blobUrl) {
        URL.revokeObjectURL(imageData.blobUrl);
      }
      this.memoryCache.delete(url);
    });
  }

  /**
   * Cleanup storage if over limit
   */
  async cleanupStorage() {
    const totalSize = await this.repository.getTotalSize();
    
    if (totalSize <= this.maxStorageSize) {
      return 0;
    }

    const deleted = await this.repository.cleanupOldImages(this.maxStorageSize);
    return deleted;
  }

  /**
   * Handle storage pressure
   */
  async handleStoragePressure() {
    console.log('[ImageCache] Handling storage pressure');
    
    // Cleanup disposable images first
    try {
      await caches.delete(this.cacheApiCacheName);
      console.log('[ImageCache] Cleared disposable image cache');
    } catch (error) {
      console.error('[ImageCache] Failed to clear disposable cache:', error);
    }

    // Then cleanup persistent images
    const deleted = await this.cleanupStorage();
    console.log('[ImageCache] Cleaned', deleted, 'persistent images');
    
    return deleted;
  }

  /**
   * Validate blob integrity
   */
  async validateBlob(blob) {
    if (!blob || blob.size === 0) {
      return false;
    }

    try {
      // Try to read first byte
      const arrayBuffer = await blob.slice(0, 1).arrayBuffer();
      return arrayBuffer.byteLength === 1;
    } catch (error) {
      console.error('[ImageCache] Blob validation failed:', error);
      return false;
    }
  }

  /**
   * Get cache statistics
   */
  async getStats() {
    const persistentSize = await this.repository.getTotalSize();
    const memorySize = this.memoryCache.size;
    
    // Get Cache API size (approximate)
    let disposableSize = 0;
    try {
      const cache = await caches.open(this.cacheApiCacheName);
      const keys = await cache.keys();
      for (const request of keys) {
        const response = await cache.match(request);
        if (response) {
          const blob = await response.blob();
          disposableSize += blob.size;
        }
      }
    } catch (error) {
      console.error('[ImageCache] Failed to get Cache API stats:', error);
    }

    return {
      memoryCached: memorySize,
      persistentStorage: persistentSize,
      disposableStorage: disposableSize,
      totalStorage: persistentSize + disposableSize,
      persistentFormatted: formatBytes(persistentSize),
      disposableFormatted: formatBytes(disposableSize),
      totalFormatted: formatBytes(persistentSize + disposableSize),
      storageLimit: formatBytes(this.maxStorageSize),
      usagePercentage: Math.round(((persistentSize + disposableSize) / this.maxStorageSize) * 100),
    };
  }

  /**
   * Get blob URL for display
   */
  getBlobUrl(cachedImage) {
    if (!cachedImage?.blob) return null;
    
    // Revoke old URL if exists
    if (cachedImage.blobUrl) {
      URL.revokeObjectURL(cachedImage.blobUrl);
    }
    
    cachedImage.blobUrl = URL.createObjectURL(cachedImage.blob);
    return cachedImage.blobUrl;
  }

  /**
   * Revoke blob URL to free memory
   */
  revokeBlobUrl(blobUrl) {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
    }
  }

  /**
   * Convert cached image to data URL
   */
  async toDataURL(cachedImage) {
    if (!cachedImage?.blob) return null;

    // Validate blob first
    const isValid = await this.validateBlob(cachedImage.blob);
    if (!isValid) {
      throw new Error('Invalid blob');
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(cachedImage.blob);
    });
  }

  /**
   * Optimize image before caching (resize, compress)
   */
  async optimizeImage(blob, options = {}) {
    const { 
      maxWidth = OfflineConfig.images.resizeMaxWidth, 
      maxHeight = OfflineConfig.images.resizeMaxHeight, 
      quality = OfflineConfig.images.compressionQuality 
    } = options;

    // Check if optimization is needed
    const dimensions = await this.getDimensions(blob);
    
    if (dimensions.width <= maxWidth && dimensions.height <= maxHeight) {
      return blob; // No optimization needed
    }

    // For now, return as-is
    // In production, implement canvas-based resizing
    console.warn('[ImageCache] Image optimization not implemented, using original');
    return blob;
  }

  /**
   * Get image dimensions
   */
  async getDimensions(blob) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      
      img.src = url;
    });
  }

  /**
   * Batch cache images from URLs
   */
  async batchCache(urls, options = {}) {
    const results = await Promise.allSettled(
      urls.map(url => this.getImage(url, options))
    );

    return {
      successful: results.filter(r => r.status === 'fulfilled').length,
      failed: results.filter(r => r.status === 'rejected').length,
    };
  }
}

// Export singleton instance
export const imageCacheService = new ImageCacheService();
